import { headPreparation, headPreparationKeys, type HeadPreparation } from './head-preparation.js';
import { hasHeadCache, installHeadCache, readHeadCache, type HeadCacheEntries, type HeadDetail } from './human-head.js';
import type { UnitDefinition } from '../../../shared/game/world-types.js';

/** One cancellable head-mesh job per view, on a worker. Keep the displayed figure while the worker sculpts its replacement. */
export class HeadMeshBuilder {
  private worker?: Worker;
  private cancel?: () => void;

  async prepare(definition: UnitDefinition, detail: HeadDetail): Promise<boolean> {
    this.stop();
    if (typeof Worker === 'undefined') return true;
    const request: HeadPreparation = headPreparation(definition, detail), keys = headPreparationKeys(request);
    if (keys.every(hasHeadCache)) return true;
    return new Promise<boolean>(resolve => {
      let worker: Worker;
      try { worker = new Worker(new URL('./head-mesh.worker.ts', import.meta.url), { type: 'module' }); }
      catch (error) { console.warn('Head mesh worker unavailable', error); resolve(true); return; }
      this.worker = worker;
      this.cancel = () => resolve(false);
      // Preserve local rendering on browsers that cannot start a module worker.
      const fail = (message: string) => { this.cancel = undefined; this.stop(); console.warn(`Head mesh worker: ${message}`); resolve(true); };
      worker.onerror = event => { if (this.worker === worker) fail(event.message); };
      worker.onmessage = (event: MessageEvent<{ entries?: HeadCacheEntries; error?: string }>) => {
        if (this.worker !== worker) return;
        if (event.data.error || !event.data.entries) { fail(event.data.error ?? 'missing surfaces'); return; }
        installHeadCache(event.data.entries);
        this.cancel = undefined;
        this.stop();
        resolve(true);
      };
      const missing = keys.filter(key => !hasHeadCache(key));
      // Fitting hair beneath headwear needs its existing base mesh, not another expensive remesh.
      const known = readHeadCache().filter(([key]) => keys.includes(key) && missing.some(next => next.startsWith(`${key}:`)));
      worker.postMessage({ request, missing, known });
    });
  }

  stop() { this.worker?.terminate(); this.worker = undefined; this.cancel?.(); this.cancel = undefined; }
}
